import React, { useState, useEffect, useCallback } from "react";
import { IconComponentProps, type TooltipPosition } from "../interface";
import defaultSvg from "../../assets/icons/svg/default.svg";
import TooltipComponent from "@CubeComponents/html/tooltip";
import { IconButton } from "@mui/material";

// `import.meta.glob` types every loader as `() => Promise<unknown>`; the SVG loader resolves to the
// asset URL, which `initIcon` checks for rather than assuming.
const iconModules: Record<string, (() => Promise<unknown>) | undefined> = import.meta.glob(
  "../../assets/icons/svg/**/*.svg",
  { import: "default" },
);

/** The placements `TooltipComponent` accepts; `IconComponentProps.position` is a bare `string`. */
/* eslint-disable logical-direction/no-physical-margin-padding -- these are MUI tooltip placement
   names mirrored from the `TooltipPosition` union, not Tailwind classes. MUI has no "start-end" /
   "end-start" placement, and the `satisfies readonly TooltipPosition[]` below fails to compile if
   they are renamed. The rule inspects every string literal, not just className attributes, which is
   why `syncfusion.interface.ts` (where the same union is declared) is in the config's ignore list. */
const TOOLTIP_POSITIONS = [
  "bottom-end",
  "bottom-start",
  "bottom",
  "left-end",
  "left-start",
  "left",
  "right-end",
  "right-start",
  "right",
  "top-end",
  "top-start",
  "top",
] as const satisfies readonly TooltipPosition[];
/* eslint-enable logical-direction/no-physical-margin-padding */

const isTooltipPosition = (value: string): value is TooltipPosition =>
  TOOLTIP_POSITIONS.some((placement) => placement === value);

/** `IconComponentProps.type` is a bare `string`; only the three DOM button types reach the button. */
const isButtonType = (value: string | undefined): value is "button" | "submit" | "reset" =>
  value === "button" || value === "submit" || value === "reset";

// Resolved icon URLs, keyed by glob path. The `import.meta.glob` loaders stay async even once the
// module is in the graph, so a freshly mounted icon always paints at least one frame with nothing
// rendered before the promise settles. Callers that recreate their React roots on every render
// (the SlickGrid cell formatters) remount icons constantly, and that empty frame reads as a
// flicker. Resolving from this cache makes every mount after the first one synchronous.
const iconSrcCache = new Map<string, string | false>();

const IconComponent: React.FC<IconComponentProps> = ({
  name,
  className = "",
  isVisible = true,
  isEnabled = true,
  showGradient = false,
  color = null,
  toolTipContent,
  position = "top",
  type,
  onClick,
  height = "1.5rem",
  width = "1.5rem",
  menuShow,
  children,
  dataTestId = "",
  parentContainerClass = "",
}) => {
  // Sizing source of truth: width/height props (default 1.5rem = 24px). Applied
  // as inline style on the rendered element so they always win over any other
  // classes the caller might add. Authors that want a different size pass
  // width="..." / height="..." rather than Tailwind w-N/h-N classes.
  const iconKey = name ? `../../assets/icons/svg/${name}.svg` : null;
  // Keyed by the icon it was loaded for, so a `name` change never paints the previous icon.
  const [loaded, setLoaded] = useState<{ key: string; src: string | false } | null>(null);
  const cachedSrc = iconKey ? iconSrcCache.get(iconKey) : undefined;
  const iconSrc: string | null | false =
    iconKey === null ? null : (cachedSrc ?? (loaded?.key === iconKey ? loaded.src : null));

  const initIcon = useCallback(async () => {
    if (!iconKey || iconSrcCache.has(iconKey)) return;
    const loader = iconModules[iconKey];
    if (!loader) {
      iconSrcCache.set(iconKey, false);
      setLoaded({ key: iconKey, src: false });
      return;
    }
    try {
      const resolved = await loader();
      const src = typeof resolved === "string" ? resolved : false;
      iconSrcCache.set(iconKey, src);
      setLoaded({ key: iconKey, src });
    } catch {
      // Not cached: a failed chunk fetch is transient, so let a later mount retry.
      setLoaded({ key: iconKey, src: false });
    }
  }, [iconKey]);

  useEffect(() => {
    initIcon();
  }, [initIcon]);

  const handleIconClick = (args: React.MouseEvent<HTMLElement>) => {
    if (isEnabled && onClick) {
      onClick(args);
    }
  };
  const iconContent = showGradient ? (
    <div
      style={{
        width,
        height,
        background: color ?? "linear-gradient(to right, #6BBBE3, #3E8AB0)",
        maskImage: `url("${iconSrc}")`,
        maskRepeat: "no-repeat",
        maskSize: "contain",
        WebkitMaskImage: `url("${iconSrc}")`,
        WebkitMaskRepeat: "no-repeat",
        WebkitMaskSize: "contain",
      }}
      className={`${className} ${isEnabled ? "" : "opacity-40"}`}
    />
  ) : (
    <img
      src={iconSrc || defaultSvg}
      alt={name || "default-icon"}
      style={{ width, height }}
      className={`${className} ${isEnabled ? "" : "opacity-40"}`}
    />
  );

  const isInteractive = !!(onClick || toolTipContent);

  const getIcon = () =>
    isInteractive ? (
      <IconButton
        data-testid={dataTestId}
        data-type={type}
        type={isButtonType(type) ? type : undefined}
        onClick={handleIconClick}
        disabled={!isEnabled}
        className={parentContainerClass}
        sx={{
          "&.Mui-focusVisible": {
            outline: "0.125rem solid #6BBBE3",
            outlineOffset: "0.125rem",
          },
          "&:hover": {
            backgroundColor: "transparent",
          },
        }}
        style={{
          cursor: isEnabled ? "pointer" : "not-allowed",
          width: "max-content",
          height: "max-content",
          verticalAlign: "top",
          padding: "0",
          display: "flex",
        }}
      >
        {iconContent}
        {children && menuShow && <>{children}</>}
      </IconButton>
    ) : (
      <span
        data-testid={dataTestId}
        className={parentContainerClass}
        style={{
          width: "max-content",
          height: "max-content",
          verticalAlign: "top",
          padding: "0",
          display: "inline-flex",
        }}
      >
        {iconContent}
        {children && menuShow && <>{children}</>}
      </span>
    );
  return isVisible ? (
    <>
      {iconSrc !== null &&
        (toolTipContent ? (
          <TooltipComponent content={toolTipContent} position={isTooltipPosition(position) ? position : "top"}>
            {getIcon()}
          </TooltipComponent>
        ) : (
          getIcon()
        ))}
    </>
  ) : (
    ""
  );
};

export default IconComponent;
